import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, alpha, Box, Button, Chip, Drawer, InputAdornment, LinearProgress, Snackbar, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { apiGetAll, apiPost } from 'api/service';
import { cancelScan, deleteScan, pauseScan, resumeScan } from 'api/features';
import { GREEN, MONO, ago, took } from './scanUi';
import { FILTERS, counters, failureText, filterJobs, isLive, resultsPath, retryBody, stateOf, whatLabel } from './jobsLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const PAGE = 50;

const StateChip = ({ scan }) => {
  const st = stateOf(scan);
  return <Chip size="small" label={st.label} sx={{ fontWeight: 700, fontSize: 11, minWidth: 66, borderRadius: 1.5, bgcolor: alpha(st.color, 0.16), color: st.color }} />;
};
const Name = ({ s }) => <>{whatLabel(s.scan_type)} on <span style={{ fontFamily: MONO }}>{s.target_value || s.target_name || 'target'}</span></>;

export default function ScanJobsPage() {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  const [limit, setLimit] = useState(PAGE);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const say = (severity, message) => setToast({ severity, message });

  const load = useCallback(async () => {
    try { setScans(await apiGetAll('/api/scan-jobs/', { maxPages: 5 })); } catch { say('error', 'Could not load scan jobs.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const live = useMemo(() => scans.filter(isLive), [scans]);
  useEffect(() => {
    if (!live.length) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [live.length, load]);

  const shown = useMemo(() => filterJobs(scans, filter, q), [scans, filter, q]);
  const shownLive = shown.filter(isLive);
  const shownDone = shown.filter((s) => !isLive(s));
  const c = counters(scans);
  const open = scans.find((s) => s.id === openId) || null;

  const act = async (s, fn, ok) => {
    setBusy(String(s.id));
    try { await fn(); if (ok) say('success', ok); await load(); } catch (e) { say('error', e?.message || 'That did not work.'); }
    setBusy('');
  };
  const rerun = (s) => act(s, () => apiPost('/api/scans/', retryBody(s)), 'Scan started again.');
  const remove = (s) => window.confirm('Delete this scan and its record?') && act(s, async () => { await deleteScan(s.id); setOpenId(null); }, 'Scan deleted.');

  const kpi = [['Running now', c.running, GREEN], ['Waiting', c.waiting, '#f59e0b'], ['Done today', c.done, 'text.primary'], ['Failed today', c.failed, c.failed ? '#ef4444' : 'text.secondary']];
  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700}>Scan jobs</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Everything the platform is scanning right now, then what finished</Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {kpi.map(([label, value, color]) => (
          <Box key={label} sx={{ ...panel, flex: '1 1 180px', p: 2.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>{label}</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO, color }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <ToggleButtonGroup exclusive size="small" value={filter} onChange={(_, v) => v && setFilter(v)}>
          {FILTERS.map((f) => <ToggleButton key={f.id} value={f.id} sx={{ textTransform: 'none', px: 2, borderRadius: '999px !important', fontWeight: 600 }}>{f.label} {scans.filter((s) => filterJobs([s], f.id, '').length).length}</ToggleButton>)}
        </ToggleButtonGroup>
        <TextField size="small" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search address or scan" sx={{ ml: 'auto', minWidth: 260 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      </Stack>

      {!loading && !shown.length && <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>{scans.length ? 'No scan matches.' : 'No scans have run yet.'}</Typography>}

      {shownLive.length > 0 && (
        <Box sx={{ ...panel, borderColor: GREEN, overflow: 'hidden', mb: 2 }}>
          <Stack direction="row" alignItems="center" gap={1.2} sx={{ px: 3, py: 2 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: GREEN }} />
            <Typography fontWeight={600}>In progress</Typography>
            <Typography variant="caption" color="text.secondary">updates every 3 seconds</Typography>
          </Stack>
          {shownLive.map((s) => (
            <Stack key={s.id} direction="row" alignItems="center" flexWrap="wrap" gap={2.5} onClick={() => setOpenId(s.id)} sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: alpha(GREEN, 0.04) } }}>
              <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                <Typography fontWeight={600}><Name s={s} /></Typography>
                <Typography variant="body2" color="text.secondary" noWrap>{s.progress_message || stateOf(s).label.toLowerCase()}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <LinearProgress variant={s.status === 'running' ? 'determinate' : 'buffer'} value={Math.min(100, Number(s.progress) || 0)} valueBuffer={0}
                  sx={{ height: 8, borderRadius: 4, bgcolor: alpha('#fff', 0.08), '& .MuiLinearProgress-bar': { bgcolor: stateOf(s).color } }} />
                <Typography variant="caption" color="text.secondary">{s.status === 'running' ? `${Math.round(Number(s.progress) || 0)}% · started ${ago(s.started_at)}` : stateOf(s).label.toLowerCase()}</Typography>
              </Box>
              {s.status === 'pending' && <Button size="small" variant="outlined" color="inherit" disabled={busy === String(s.id)} onClick={(e) => { e.stopPropagation(); act(s, () => pauseScan(s.id), 'Scan paused.'); }}>Pause</Button>}
              {s.status === 'paused' && <Button size="small" variant="outlined" color="inherit" disabled={busy === String(s.id)} onClick={(e) => { e.stopPropagation(); act(s, () => resumeScan(s.id), 'Scan resumed.'); }}>Resume</Button>}
              <Button size="small" variant="outlined" color="error" disabled={busy === String(s.id)} onClick={(e) => { e.stopPropagation(); act(s, () => cancelScan(s.id), 'Scan stopped.'); }}>Stop</Button>
            </Stack>
          ))}
        </Box>
      )}

      {shownDone.length > 0 && (
        <Box sx={{ ...panel, overflow: 'hidden' }}>
          <Typography fontWeight={600} sx={{ px: 3, py: 2 }}>Finished</Typography>
          {shownDone.slice(0, limit).map((s) => (
            <Stack key={s.id} direction="row" alignItems="center" flexWrap="wrap" gap={2} onClick={() => setOpenId(s.id)} sx={{ px: 3, py: 1.8, borderTop: 1, borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: alpha(GREEN, 0.04) } }}>
              <StateChip scan={s} />
              <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                <Typography fontWeight={600}><Name s={s} /></Typography>
                <Typography variant="body2" noWrap sx={{ color: s.status === 'failed' ? '#ef4444' : 'text.secondary' }}>
                  {s.status === 'failed' ? failureText(s) : s.status === 'completed' ? `Finished in ${took(s.started_at, s.finished_at) || 'a moment'}` : 'Stopped by you'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>{ago(s.finished_at || s.created_at)}</Typography>
              <Button size="small" variant="outlined" color="inherit" disabled={busy === String(s.id)} onClick={(e) => { e.stopPropagation(); rerun(s); }}>{s.status === 'failed' ? 'Try again' : 'Run again'}</Button>
            </Stack>
          ))}
          {shownDone.length > limit && <Button fullWidth onClick={() => setLimit((n) => n + PAGE)} sx={{ py: 1.5, color: GREEN, fontWeight: 700 }}>Show more ({shownDone.length - limit} left)</Button>}
        </Box>
      )}

      <Drawer anchor="right" open={!!open} onClose={() => setOpenId(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 3 } }}>
        {open && (
          <Stack gap={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>{whatLabel(open.scan_type)} on {open.target_value}</Typography>
              <Typography variant="body2" color="text.secondary">Started {ago(open.started_at || open.created_at)}{open.finished_at ? ` · took ${took(open.started_at, open.finished_at) || 'a moment'}` : ''}</Typography>
            </Box>
            <StateChip scan={open} />
            {isLive(open) && <Alert severity="info" icon={false}>{open.progress_message || 'Working on it.'}</Alert>}
            {(open.status === 'failed' || open.status === 'cancelled') && (
              <Box sx={{ p: 2, borderRadius: 2, border: 1, borderColor: alpha('#ef4444', 0.35), bgcolor: alpha('#ef4444', 0.08) }}>
                <Typography fontWeight={700} sx={{ mb: 0.5 }}>{open.status === 'failed' ? 'What went wrong' : 'Stopped'}</Typography>
                <Typography variant="body2">{failureText(open)}</Typography>
              </Box>
            )}
            <Stack direction="row" gap={1}>
              {!isLive(open) && <Button variant="contained" fullWidth disabled={busy === String(open.id)} onClick={() => rerun(open)} sx={{ fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>{open.status === 'failed' ? 'Try again' : 'Run again'}</Button>}
              {open.status === 'completed' && <Button variant="outlined" color="inherit" onClick={() => navigate(resultsPath(open.scan_type))}>Open results</Button>}
              {!isLive(open) && <Button color="error" variant="outlined" disabled={busy === String(open.id)} onClick={() => remove(open)}>Delete</Button>}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>Job #{open.id} · phase: {open.phase || '-'}</Typography>
          </Stack>
        )}
      </Drawer>
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Box>
  );
}
